import type { Identifier } from "ra-core";

import { ASSET_KINDS, type AssetKind } from "./meetingAssets";

export type MeetingLinkTarget = {
  /** The asset to open the meeting on. */
  asset?: AssetKind;
  /** A moment in the transcript's timeline, in seconds. */
  t?: number;
  /**
   * The summary to show, by its summaryEntries() key ("recap", "topics",
   * "next" or a saved summary's id). From a summary search excerpt,
   * LCATOMICZO-76 phase 5.
   */
  summary?: string;
};

/**
 * The one way to open a meeting at an asset or a moment:
 * `/zoom_meetings/:id/show?asset=transcript&t=123`. The list's asset icons use
 * it, and transcript search excerpts will (LCATOMICZO-76 phase 4).
 */
export const meetingLink = (
  id: Identifier,
  { asset, t, summary }: MeetingLinkTarget = {},
): string => {
  const params = new URLSearchParams();
  if (asset) params.set("asset", asset);
  if (t != null) params.set("t", String(Math.floor(t)));
  if (summary) params.set("summary", summary);
  const query = params.toString();
  return `/zoom_meetings/${id}/show${query ? `?${query}` : ""}`;
};

/**
 * Reads a meeting link back. Anything it does not recognise is dropped rather
 * than passed on, so a hand-edited or stale URL opens the meeting normally.
 */
export const parseMeetingLink = (
  params: URLSearchParams,
): MeetingLinkTarget => {
  const out: MeetingLinkTarget = {};
  const asset = params.get("asset");
  if (asset && (ASSET_KINDS as readonly string[]).includes(asset)) {
    out.asset = asset as AssetKind;
  }
  const raw = params.get("t");
  if (raw != null && raw.trim() !== "") {
    const t = Number(raw);
    if (Number.isFinite(t) && t >= 0) out.t = t;
  }
  const summary = params.get("summary")?.trim();
  if (summary) out.summary = summary;
  return out;
};

/**
 * The same link as a full URL someone can paste into a browser. The app routes
 * with a hash router, so the route sits after `#` and the document path is
 * whatever the app is served from.
 */
export const absoluteMeetingLink = (
  id: Identifier,
  target: MeetingLinkTarget,
  location: Pick<Location, "origin" | "pathname"> = window.location,
): string =>
  `${location.origin}${location.pathname}#${meetingLink(id, target)}`;
